import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Video, BookOpen, Brain, Award, CheckCircle, Clock, Users, Target } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import InteractiveBookCTA from '../components/InteractiveBookCTA';
import VittaCIREPathways from '../components/VittaCIREPathways';

const CIREExam = () => {
  const cireStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "CIRE Exam Prep Course - Canadian Investment Regulatory Exam",
    "description": "Comprehensive preparation for the Canadian Investment Regulatory Exam (CIRE). Master all 9 CIRO elements with expert-led video lessons, AI tutor, practice questions, and full mock exams.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Vitta Finance Academy",
      "url": "https://www.vittafinance.ca"
    },
    "courseCode": "CIRE-PREP",
    "educationalLevel": "Professional",
    "about": ["Canadian Investment Regulatory Exam", "CIRO Proficiency Exam", "Registered Representative Certification"],
    "teaches": ["Investment regulations", "KYC/KYP", "Securities products", "Derivatives", "Ethics and compliance"],
    "numberOfCredits": "110 questions in 2 hours",
    "occupationalCredentialAwarded": "Registered Representative (RR) / Investment Representative (IR)",
    "offers": {
      "@type": "Offer",
      "price": "29.99",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "PT45H"
    }
  };

  const cireFaqData = [
    {
      question: "What is the CIRE exam?",
      answer: "The Canadian Investment Regulatory Exam (CIRE) is CIRO's proficiency requirement for individuals seeking registration as an Investment Representative or Registered Representative in Canada. It covers 9 key elements including investment products, regulations, KYC/KYP requirements, and ethics."
    },
    {
      question: "How many questions are on the CIRE exam?",
      answer: "The CIRE exam has 110 multiple-choice questions that must be completed in 2 hours. The passing score is determined by CIRO for each exam sitting."
    },
    {
      question: "How long should I study for the CIRE exam?",
      answer: "We recommend 4-6 weeks of dedicated study time, spending 2-3 hours daily. Our comprehensive course includes a study schedule to help you prepare effectively."
    },
    {
      question: "What is the passing score for the CIRE exam?",
      answer: "The passing score for the CIRE exam is determined by CIRO for each exam sitting. CIRO uses a standardized scoring method to ensure fairness across different exam versions."
    },
    {
      question: "How do I register for the CIRE exam?",
      answer: "You can register for the CIRE exam through CIRO's official website or through your sponsoring firm. Exams are administered at Prometric testing centers across Canada."
    },
    {
      question: "What topics are covered on the CIRE exam?",
      answer: "The CIRE exam covers 9 key elements: Regulatory Framework, Client Relationships, KYC/KYP Requirements, Investment Products, Equity Securities, Debt Securities, Derivatives, Managed Products, and Ethics & Professional Conduct."
    },
    {
      question: "Can I retake the CIRE exam if I fail?",
      answer: "Yes, you can retake the CIRE exam. There is typically a waiting period of 30 days before you can reattempt. We recommend reviewing your weak areas before retaking."
    },
    {
      question: "What study materials do I need for the CIRE exam?",
      answer: "Essential study materials include a comprehensive study guide covering all 9 elements, practice question banks, mock exams, and video lessons. Vitta Finance Academy offers all these resources in our complete CIRE prep course."
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="CIRE Exam Prep 2025 - Canadian Investment Regulatory Exam Course | Pass Guaranteed"
        description="Pass the CIRE exam on your first attempt. Canada's #1 Canadian Investment Regulatory Exam prep course. 110 questions, 2 hours, 9 CIRO elements covered. Expert video lessons, AI tutor, 800+ practice questions, 5 full mock exams, 700-page study guide. Industry leading pass rate. Prep for Registered Representative (RR) & Investment Representative (IR) certification."
        keywords="CIRE exam, Canadian Investment Regulatory Exam, CIRE exam prep, CIRE syllabus, CIRE practice exam, CIRE sample questions, CIRE study guide, pass CIRE exam, CIRE exam registration, CIRE exam tips, how to pass CIRE exam, CIRE exam format, CIRE exam fees, CIRE mock exam, CIRE question bank, CIRE notes, CIRE cheat sheet, CIRE pass score, CIRE passing score, CIRE exam dates, CIRE 30 day study plan, CIRE 60 day study plan, CIRE exam prep Canada, CIRE exam prep Alberta, CIRE exam prep Ontario, CIRE exam prep Toronto, registered representative exam, investment representative exam, CIRO CIRE, CIRO proficiency requirements registered representative"
        ogTitle="CIRE Exam Prep Course - Pass Your Canadian Investment Regulatory Exam"
        ogDescription="Complete CIRE exam preparation. Expert instructors, 800+ practice questions, 5 full mock exams, AI tutor. Join 30,000+ successful candidates. Industry leading pass rate."
        structuredData={cireStructuredData}
        faqData={cireFaqData}
        pageType="course"
      />

      {/* Hero Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'Courses', link: '/courses' },
            { label: 'CIRE Exam' }
          ]} />
          
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '24px',
            color: 'white'
          }}>
            Canadian Investment Regulatory Exam (CIRE)
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            maxWidth: '800px',
            opacity: 0.95
          }}>
            Complete exam prep for aspiring Registered Representatives (RR) and Investment Representatives (IR). Master all 9 CIRO elements with confidence.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <a href="https://vittafinance.mykajabi.com/offers/5Tzz73ay" target="_blank" rel="noopener noreferrer" className="btn-gold">
              Enroll Now <ArrowRight size={20} />
            </a>
            <Link to="/contact" className="btn-secondary" style={{
              backgroundColor: 'white',
              color: 'var(--brand-primary)',
              borderColor: 'white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{
        padding: '40px 0',
        backgroundColor: 'var(--neutral-light)',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '30px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: '8px' }}>110</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Questions</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: '8px' }}>2 Hours</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Exam Duration</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: '8px' }}>9</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>CIRO Elements</div>
            </div>
          </div>
        </div>
      </section>

      {/* About CIRE Exam */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>About the CIRE Exam</h2>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              The <strong>Canadian Investment Regulatory Exam (CIRE)</strong> is the mandatory proficiency requirement for anyone seeking <strong>Registered Representative (RR)</strong> or <strong>Investment Representative (IR)</strong> registration in Canada.
            </p>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              Administered by <strong>CIRO (Canadian Investment Regulatory Organization)</strong>, the exam tests your ability to apply regulatory knowledge in real-world scenarios across 9 key elements.
            </p>
          </div>
        </div>
      </section>

      {/* 9 CIRO Elements */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>9 CIRO Elements Covered</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {[
              { num: 1, title: 'Regulatory Framework', desc: 'Understanding CIRO rules and Canadian securities law' },
              { num: 2, title: 'KYC & Suitability', desc: 'Know Your Client and suitability assessments' },
              { num: 3, title: 'Account Scope & Authority', desc: 'Account types and trading authorization' },
              { num: 4, title: 'Complaints & Disputes', desc: 'Client complaint handling procedures' },
              { num: 5, title: 'Market & Company Analysis', desc: 'Fundamental and technical analysis' },
              { num: 6, title: 'Market Integrity & Settlement', desc: 'Trade execution and settlement processes' },
              { num: 7, title: 'Securities & Managed Products', desc: 'Stocks, bonds, mutual funds, ETFs' },
              { num: 8, title: 'Derivatives', desc: 'Options, futures, and derivative strategies' },
              { num: 9, title: 'Conflicts & Ethics', desc: 'Professional conduct and conflict management' }
            ].map((element) => (
              <div key={element.num} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--brand-primary)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 900,
                  marginBottom: '16px'
                }}>
                  {element.num}
                </div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{element.title}</h3>
                <p className="body-small">{element.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Pathways */}
      <VittaCIREPathways />

      {/* Course Features */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>Complete CIRE Exam Prep Course</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                icon: <Video size={40} color="var(--brand-primary)" />,
                title: 'Instructor-Led Videos',
                desc: 'Expert instructors break down complex topics into easy-to-understand lessons'
              },
              {
                icon: <Brain size={40} color="var(--brand-primary)" />,
                title: '24/7 AI Tutor',
                desc: 'Get instant answers to your questions anytime with our CIRO-trained AI assistant'
              },
              {
                icon: <BookOpen size={40} color="var(--brand-primary)" />,
                title: 'Practice Questions',
                desc: 'Hundreds of exam-style MCQs with detailed explanations for every answer'
              },
              {
                icon: <Award size={40} color="var(--brand-primary)" />,
                title: 'Full Mock Exams',
                desc: 'Realistic 110-question practice exams that mirror actual CIRE format'
              },
              {
                icon: <Clock size={40} color="var(--brand-primary)" />,
                title: 'Timed Practice',
                desc: 'Build exam stamina with timed mock tests under real conditions'
              },
              {
                icon: <Target size={40} color="var(--brand-primary)" />,
                title: 'Comprehensive Study Book',
                desc: '700+ page printed guide covering all exam topics with in-depth explanations and case studies'
              },
              {
                icon: <Users size={40} color="var(--brand-primary)" />,
                title: 'Expert Support',
                desc: 'Access to instructors with 40+ years of teaching and industry experience'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                backgroundColor: 'var(--neutral-light)',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ marginBottom: '16px' }}>{feature.icon}</div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{feature.title}</h3>
                <p className="body-small">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Materials */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>CIRE Study Materials on Amazon</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: 'Printed CIRE Study Guide',
                price: '299.99',
                pages: '700',
                link: 'https://www.amazon.ca/dp/B0FV3J7PJJ',
                desc: 'Complete coverage of all 9 CIRO elements with case studies'
              },
              {
                title: 'CIRE 5 Full-Length Mock Exams',
                price: '49.99',
                questions: '550',
                link: 'https://www.amazon.ca/dp/B0FWK36ZND',
                desc: '5 realistic practice exams with detailed answer explanations'
              },
              {
                title: 'CIRE Quick Study Guide',
                price: '34.99',
                concepts: '200',
                link: 'https://www.amazon.ca/dp/B0GC5Z4RVM',
                desc: 'Top 200 concepts for fast, focused final review'
              },
              {
                title: 'CIRE Interactive Quick Study Guide',
                price: '34.99',
                features: '9 Interactive Elements',
                link: '/books/cire-interactive',
                desc: 'Master the Canadian Investment Regulatory Examination with our revolutionary interactive study system. 9 comprehensive elements with visual learning tools, practice questions, and real-time feedback.',
                badges: ['Online', 'Visual Learning', 'Lifetime Access'],
                isInternal: true
              }
            ].map((product, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }} className="card-hover-effect">
                {product.isInternal && (
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '20px',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '700'
                  }}>NEW</div>
                )}
                <h3 className="heading-5" style={{ marginBottom: '16px' }}>{product.title}</h3>
                <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {product.pages && <span className="caption" style={{ backgroundColor: 'var(--neutral-light)', padding: '6px 12px', borderRadius: '4px' }}>{product.pages} Pages</span>}
                  {product.questions && <span className="caption" style={{ backgroundColor: 'var(--neutral-light)', padding: '6px 12px', borderRadius: '4px' }}>{product.questions} Questions</span>}
                  {product.concepts && <span className="caption" style={{ backgroundColor: 'var(--neutral-light)', padding: '6px 12px', borderRadius: '4px' }}>Top {product.concepts} Concepts</span>}
                  {product.features && <span className="caption" style={{ backgroundColor: '#D4AF3720', color: '#B8860B', padding: '6px 12px', borderRadius: '4px', fontWeight: 600 }}>{product.features}</span>}
                  {product.badges && product.badges.map((badge, badgeIdx) => (
                    <span key={badgeIdx} className="caption" style={{ backgroundColor: '#D4AF3720', color: '#B8860B', padding: '6px 12px', borderRadius: '4px', fontWeight: 600 }}>{badge}</span>
                  ))}
                </div>
                <p className="body-small" style={{ marginBottom: '24px', flex: 1 }}>{product.desc}</p>
                <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: '16px' }}>CAD ${product.price}</div>
                {product.isInternal ? (
                  <Link to={product.link} className="btn-primary" style={{ width: '100%', textAlign: 'center', textDecoration: 'none' }}>Explore Interactive Book</Link>
                ) : (
                  <a href={product.link} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ width: '100%' }}>Buy on Amazon</a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Book CTA */}
      <InteractiveBookCTA />

      {/* Pricing */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>CIRE Online Course Pricing</h2>
          <div style={{
            maxWidth: '450px',
            margin: '0 auto'
          }}>
            <div style={{
              backgroundColor: 'var(--brand-primary)',
              padding: '48px',
              borderRadius: '16px',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 12px 40px rgba(30, 58, 138, 0.25)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-14px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--gold-primary)',
                color: 'white',
                padding: '8px 24px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              }}>BEST VALUE</div>
              <h3 className="heading-4" style={{ marginBottom: '16px', color: 'white' }}>Monthly Plan</h3>
              <div style={{ fontSize: '3.5rem', fontWeight: 900, color: 'var(--gold-primary)', marginBottom: '8px' }}>$29.99</div>
              <p className="body-small" style={{ marginBottom: '32px', color: 'rgba(255,255,255,0.8)' }}>per month • Cancel anytime</p>
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '32px', textAlign: 'left' }}>
                {['All video lessons', 'AI Tutor access', 'Practice questions', 'Mock exams', 'Study guides'].map((feature, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <CheckCircle size={22} color="var(--gold-primary)" />
                    <span className="body-small" style={{ color: 'white', fontSize: '1rem' }}>{feature}</span>
                  </li>
                ))}
              </ul>
              <a href="https://vittafinance.mykajabi.com/offers/5Tzz73ay" target="_blank" rel="noopener noreferrer" className="btn-gold" style={{ width: '100%', padding: '16px 32px', fontSize: '1.1rem' }}>Get Started</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 48px'
          }}>
            Everything you need to know about the CIRE exam
          </p>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {cireFaqData.map((faq, idx) => (
              <details 
                key={idx}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
              >
                <summary style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: 'var(--text-primary)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {faq.question}
                  <span style={{ 
                    fontSize: '1.5rem', 
                    color: 'var(--brand-primary)',
                    fontWeight: 400
                  }}>+</span>
                </summary>
                <div style={{
                  padding: '0 24px 20px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7
                }}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>Ready to Pass Your CIRE Exam?</h2>
          <p style={{ 
            fontSize: '1.25rem',
            lineHeight: 1.7,
            marginBottom: '40px', 
            maxWidth: '700px', 
            margin: '0 auto 40px',
            color: 'white',
            fontWeight: 500
          }}>
            Join thousands of successful Registered Representatives who passed with Vitta Finance Academy
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://vittafinance.mykajabi.com/offers/5Tzz73ay" target="_blank" rel="noopener noreferrer" className="btn-gold">
              Enroll in CIRE Course <ArrowRight size={20} />
            </a>
            <Link to="/contact" className="btn-secondary" style={{ backgroundColor: 'white', color: 'var(--brand-primary)', borderColor: 'white' }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CIREExam;